// mattermost-command-parser.ts — F07 (phase-07 tests 1-11): turns an
// accepted Mattermost post into one of the 8 control directives (status,
// approve, pause, resume, redirect, answer, revert, abort/abort confirm).
// Extends P4b's mattermost-directive-parser.ts acceptance filter (bot/quote/
// code-fence exclusion) with: channel id check, mission-creation-time check,
// per-command payload parsing (mattermost-command-body-parser.ts, split out
// for file-size), unknown-command throttled hint, missing approve
// request-id prompt, and abort-confirm actor+TTL binding.
//
// Authority NEVER comes from message text/display name — only from
// `post.user_id === boundUserId` (spec: "Không lấy quyền từ display name hay
// nội dung 'Luke đã nói'"). Content claiming authority in prose is just text.
//
// Frozen output: directive content + sha256 via canonical-json-and-checksum
// (reused, not reimplemented) so a later edit/delete of the source post
// cannot retroactively change what was approved (F07 follow-up in P5).
import { canonicalJson, sha256Checksum } from "./canonical-json-and-checksum.js";
import { type CommandKind, parseCommandBody } from "./mattermost-command-body-parser.js";
import type { MattermostPost } from "./mattermost-rest-client.js";

export type { CommandKind } from "./mattermost-command-body-parser.js";

export interface ParsedCommandDirective {
  kind: CommandKind;
  payload: Record<string, unknown>;
  raw: string;
  content_sha256: string;
}

export type CommandRejectionReason =
  | "bot_post"
  | "wrong_user"
  | "wrong_channel"
  | "wrong_thread"
  | "before_mission_creation"
  | "quoted_or_code_block"
  | "not_a_command"
  | "unknown_command"
  | "missing_request_id"
  | "legacy_answer_form"
  | "abort_confirm_expired_or_wrong_actor";

export interface CommandParseOk {
  ok: true;
  directive: ParsedCommandDirective;
}

export interface CommandParseErr {
  ok: false;
  reason: CommandRejectionReason;
  /** Only set when reason === "unknown_command": whether the throttle allows a hint reply now. */
  shouldReplyHint?: boolean;
  /** Only set when reason === "missing_request_id". */
  promptedRequestId?: string;
}

export type CommandParseResult = CommandParseOk | CommandParseErr;

export interface MattermostCommandParserContext {
  botUserId: string;
  boundUserId: string;
  expectedChannelId: string;
  expectedRootId: string;
  missionCreatedAtMs: number;
  hintThrottleSec: number;
}

export function buildCommandParserContext(
  input: MattermostCommandParserContext,
): MattermostCommandParserContext {
  return { ...input };
}

export interface ParseMattermostCommandOptions {
  /** Epoch ms clock the caller controls (tests inject deterministic values); defaults to Date.now(). */
  nowMs?: number;
  /** Epoch ms of the last unknown-command hint reply, if any — for throttle. */
  lastHintAtMs?: number;
  /** The request id currently awaiting approval, if any — for the "approve without id" prompt. */
  currentRequestId?: string;
  /** abort_confirm binding: when the original `abort` was issued, by whom, and its TTL. */
  abortIssuedAtMs?: number;
  abortIssuedByUserId?: string;
  abortChallengeTtlSec?: number;
}

const QUOTE_OR_FENCE_LINE = /^>/;
const DIRECTIVE_LINE = /^@avatar\s+(.+)$/;

/** Strips quoted (`>`) and fenced (```) lines the same way P4b's directive parser does. */
function extractCommandLine(message: string): string | undefined {
  const lines = message.split("\n");
  let inFence = false;
  for (const line of lines) {
    const trimmed = line.trim();
    if (trimmed.startsWith("```")) {
      inFence = !inFence;
      continue;
    }
    if (inFence) continue;
    if (QUOTE_OR_FENCE_LINE.test(trimmed)) continue;
    const match = DIRECTIVE_LINE.exec(trimmed);
    if (match?.[1]) return match[1].trim();
  }
  return undefined;
}

function freeze(
  kind: CommandKind,
  payload: Record<string, unknown>,
  raw: string,
): ParsedCommandDirective {
  const content_sha256 = sha256Checksum(canonicalJson({ kind, payload, raw }));
  return { kind, payload, raw, content_sha256 };
}

export function parseMattermostCommand(
  post: MattermostPost,
  context: MattermostCommandParserContext,
  options: ParseMattermostCommandOptions = {},
): CommandParseResult {
  if (post.user_id === context.botUserId) {
    return { ok: false, reason: "bot_post" };
  }
  if (post.user_id !== context.boundUserId) {
    return { ok: false, reason: "wrong_user" };
  }
  if (post.channel_id !== context.expectedChannelId) {
    return { ok: false, reason: "wrong_channel" };
  }
  const threadRootId = post.root_id === "" ? post.id : post.root_id;
  if (threadRootId !== context.expectedRootId) {
    return { ok: false, reason: "wrong_thread" };
  }
  if (post.create_at <= context.missionCreatedAtMs) {
    return { ok: false, reason: "before_mission_creation" };
  }

  const commandLine = extractCommandLine(post.message);
  if (commandLine === undefined) {
    return { ok: false, reason: "not_a_command" };
  }

  // abort_confirm has its own actor+TTL binding on top of the generic filter.
  const abortConfirmMatch = /^abort\s+confirm\s+(\S+)$/.exec(commandLine);
  if (abortConfirmMatch?.[1]) {
    const challengeId = abortConfirmMatch[1];
    const nowMs = options.nowMs ?? Date.now();
    const sameActor = options.abortIssuedByUserId === context.boundUserId;
    const withinTtl =
      options.abortIssuedAtMs !== undefined &&
      options.abortChallengeTtlSec !== undefined &&
      nowMs - options.abortIssuedAtMs <= options.abortChallengeTtlSec * 1000;
    if (!sameActor || !withinTtl) {
      return { ok: false, reason: "abort_confirm_expired_or_wrong_actor" };
    }
    return {
      ok: true,
      directive: freeze("abort_confirm", { challenge_id: challengeId }, post.message),
    };
  }

  const parsed = parseCommandBody(commandLine);
  if (parsed === "missing_request_id") {
    return {
      ok: false,
      reason: "missing_request_id",
      promptedRequestId: options.currentRequestId,
    };
  }
  if (parsed === "legacy_answer_form") {
    return { ok: false, reason: "legacy_answer_form" };
  }
  if (parsed === undefined) {
    const nowMs = options.nowMs ?? Date.now();
    const shouldReplyHint =
      options.lastHintAtMs === undefined ||
      nowMs - options.lastHintAtMs >= context.hintThrottleSec * 1000;
    return { ok: false, reason: "unknown_command", shouldReplyHint };
  }

  return { ok: true, directive: freeze(parsed.kind, parsed.payload, post.message) };
}
